export interface UserProfile {
  id: string;
  username: string;
  email?: string;
  avatarUrl?: string;
  createdAt?: number;
  lastActiveAt?: number;
  takesCount?: number;
  totalDuration?: number;
  entriesCount?: number;
  clipsCount?: number;
  lastTakeAt?: number;
  lastEntryAt?: number;
  lastClipAt?: number;
  isGuest?: boolean;
}

export interface UserVaultData {
  user: UserProfile | null;
  clips: any[];
  entries: any[];
  stats: {
    clipsCount: number;
    entriesCount: number;
    takesCount: number;
  };
}

export interface AuthResponse {
  success: boolean;
  user?: UserProfile;
  error?: string;
  notRegistered?: boolean;
}

export async function fetchUserVault(userName: string): Promise<UserVaultData | null> {
  if (!userName?.trim()) return null;
  try {
    const res = await fetch(`/api/vault?user=${encodeURIComponent(userName.trim())}`);
    if (res.ok) {
      const data = await res.json() as any;
      if (data.success && data.vault) {
        return data.vault;
      }
    }
  } catch (err) {
    console.warn('[vaultService] fetchUserVault notice:', err);
  }
  return null;
}

export async function fetchUserProfile(username: string): Promise<UserProfile | null> {
  const clean = username?.trim();
  if (!clean) return null;
  try {
    const res = await fetch(`/api/user/session?username=${encodeURIComponent(clean)}`);
    if (res.ok) {
      const data = await res.json() as any;
      if (data.success && data.user) {
        return data.user;
      }
    }
  } catch (err) {
    console.warn('[vaultService] fetchUserProfile notice:', err);
  }
  return null;
}

export async function fetchRegisteredUsers(): Promise<UserProfile[]> {
  try {
    const res = await fetch('/api/user/session?list=true');
    if (res.ok) {
      const data = await res.json() as any;
      if (data.success && Array.isArray(data.users)) {
        return data.users;
      }
    }
  } catch (err) {
    console.warn('[vaultService] fetchRegisteredUsers notice:', err);
  }
  return [];
}

export async function checkCurrentSession(): Promise<UserProfile | null> {
  try {
    const res = await fetch('/api/user/session?self=true', {
      credentials: 'include'
    });
    if (res.ok) {
      const data = await res.json() as any;
      if (data.success && data.authenticated && data.user) {
        return data.user;
      }
    }
  } catch (err) {
    console.warn('[vaultService] checkCurrentSession notice:', err);
  }
  return null;
}

export async function logoutUserSession(): Promise<boolean> {
  try {
    const res = await fetch('/api/user/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ action: 'logout' })
    });
    if (res.ok) {
      const data = await res.json() as any;
      return !!data.success;
    }
  } catch (err) {
    console.warn('[vaultService] logoutUserSession notice:', err);
  }
  return false;
}

export async function loginUserSession(username: string, password?: string): Promise<AuthResponse> {
  const clean = username?.trim();
  if (!clean) return { success: false, error: 'Username is required' };
  try {
    const res = await fetch('/api/user/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        action: 'login',
        username: clean,
        password: password?.trim() || ''
      })
    });
    const data = await res.json() as any;
    if (res.ok && data.success && data.user) {
      return { success: true, user: data.user };
    }
    return {
      success: false,
      error: data.error || 'Login failed',
      notRegistered: !!data.notRegistered
    };
  } catch (err: any) {
    console.warn('[vaultService] loginUserSession notice:', err);
    return { success: false, error: err.message || 'Network error connecting to server' };
  }
}

export async function registerUserSession(
  username: string,
  password?: string,
  extra?: { email?: string; avatarUrl?: string }
): Promise<AuthResponse> {
  const clean = username?.trim();
  if (!clean) return { success: false, error: 'Username is required' };
  try {
    const res = await fetch('/api/user/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        action: 'register',
        username: clean,
        password: password?.trim() || '',
        email: extra?.email?.trim() || '',
        avatarUrl: extra?.avatarUrl?.trim() || 'char-maverick'
      })
    });
    const data = await res.json() as any;
    if (res.ok && data.success && data.user) {
      return { success: true, user: data.user };
    }
    return {
      success: false,
      error: data.error || 'Registration failed'
    };
  } catch (err: any) {
    console.warn('[vaultService] registerUserSession notice:', err);
    return { success: false, error: err.message || 'Network error connecting to server' };
  }
}

export async function updateUserProfile(
  username: string,
  updates: { email?: string; avatarUrl?: string }
): Promise<AuthResponse> {
  const clean = username?.trim();
  if (!clean) return { success: false, error: 'Username is required' };
  try {
    const res = await fetch('/api/user/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        action: 'update',
        username: clean,
        email: updates.email,
        avatarUrl: updates.avatarUrl
      })
    });
    const data = await res.json() as any;
    if (res.ok && data.success && data.user) {
      return { success: true, user: data.user };
    }
    return { success: false, error: data.error || 'Update failed' };
  } catch (err: any) {
    console.warn('[vaultService] updateUserProfile notice:', err);
    return { success: false, error: err.message || 'Network error connecting to server' };
  }
}
